import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Supplier } from 'api/supplier/suppliers.types';
import { supplierGroupsAPI } from 'api/supplierGroup/supplierGroupAPI';
import { SupplierVehicle } from 'api/supplierGroup/suppliergroup.types';
import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSupplier } from 'reducers/suppliersReducer';
import { IntegrationWizardData } from '../IntegrationSetup';

interface CredentialValidationProps {
  supplier: Supplier
  wizardData: IntegrationWizardData
  onSuccess: () => void
  onBack: () => void
}

const CredentialValidation: FC<CredentialValidationProps> = ({
  supplier,
  wizardData,
  onSuccess,
  onBack,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [supplierVehicleOptions, setSupplierVehicleOptions] = useState<
    SupplierVehicle[]
  >([])
  const [errorMessage, setErrorMessage] = useState<string>()
  const dispatch = useDispatch()

  useEffect(() => {
    const saveAndValidate = async () => {
      try {
        const payload = {
          endPoint: wizardData.endpoint!,
          dispatchReference: wizardData.dispatchReference,
          aPIKey: wizardData.apiKey,
          appKey: wizardData.appKey,
          appSecret: wizardData.appSecret,
          accountNumber: wizardData.accountNumber,
          unmanagedAccountNumber: wizardData.unmanagedAccountNumber,
        }

        const selectedGroupId = Number(wizardData.supplierGroupId)

        if (wizardData.previousSupplierGroupId !== undefined) {
          await supplierGroupsAPI.removeSupplierFromGroup(
            wizardData.previousSupplierGroupId,
            supplier.id,
          )
          await supplierGroupsAPI.addSupplierToGroup(selectedGroupId, {
            supplierId: supplier.id,
            ...payload,
          })
          dispatch(
            setSupplier({ ...supplier, supplierGroupId: selectedGroupId }),
          )
        } else if (wizardData.isExistingConfig) {
          await supplierGroupsAPI.updateSupplierInGroup(
            selectedGroupId,
            supplier.id,
            payload,
          )
        } else {
          await supplierGroupsAPI.addSupplierToGroup(selectedGroupId, {
            supplierId: supplier.id,
            ...payload,
          })
          dispatch(
            setSupplier({ ...supplier, supplierGroupId: selectedGroupId }),
          )
        }

        const response = await supplierGroupsAPI.retrieveSupplierVehicleOptions(
          selectedGroupId,
          supplier.id,
        )
        setSupplierVehicleOptions(response.data.value)
      } catch {
        setErrorMessage(
          'Credentials could not be validated. Please go back and check your entries.',
        )
      } finally {
        setIsLoading(false)
      }
    }

    saveAndValidate()
  }, [])

  const succeeded = !isLoading && !errorMessage

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Validating credentials...</Typography>
        </Box>
      )}
      {succeeded && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CheckCircleOutlineIcon color="success" />
          <Typography>
            Credentials validated - {supplierVehicleOptions.length} vehicle
            option
            {supplierVehicleOptions.length !== 1 ? 's' : ''} available from
            dispatch platform.
          </Typography>
        </Box>
      )}
      {errorMessage && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ErrorOutlineIcon color="error" />
          <Typography color="error">{errorMessage}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button onClick={onBack} disabled={isLoading}>
          Back
        </Button>
        <Button variant="contained" onClick={onSuccess} disabled={!succeeded}>
          Next
        </Button>
      </Box>
    </Box>
  )
}

export default CredentialValidation
